/* eslint-disable @typescript-eslint/no-empty-function */
import _debounce from 'lodash/debounce';
import { getBreakpoint, isElementInView } from './_utils';
import triggerAnalyticsEvent from '../analytics/analytics';

const selectors = {
	video: '.Video-video',
	playBtn: '.Video-playButton',
};
const classes = {
	paused: 'Video--paused',
	playIcon: 'Button--playIcon--',
	pauseIcon: 'Button--pauseIcon--',
};

/**
 * Returns a function that initializes a single video element, adding a src value.
 * @param  {Element}  videoElement    The video element
 * @return {Function}               	The function that initializes the video element
 */
function getInitVideo(videoElement) {
	const $videoElement = $(videoElement);
	const videoURL = $videoElement.data('videoUrl');

	return function () {
		// Check if we're in large or xlarge
		const currentBreakpoint = getBreakpoint();
		const bpIsDesktop = currentBreakpoint === 'large' || currentBreakpoint === 'xlarge';
		const bpIsTablet = currentBreakpoint === 'medium';
		const videoSource = $videoElement.attr('src');
		const hasSource = typeof videoSource === 'string' && videoSource.length > 0;
		const mobileVideo = $videoElement.hasClass('video-mobile');
		const tabletVideo = $videoElement.hasClass('video-tablet');

		if (mobileVideo) {
			if (!bpIsDesktop && !bpIsTablet && !hasSource) {
				$videoElement.attr('src', videoURL);
			} else if (bpIsDesktop || bpIsTablet) {
				$videoElement.removeAttr('src');
			}
		} else if (tabletVideo) {
			if (bpIsTablet && !hasSource) {
				$videoElement.attr('src', videoURL);
			} else if (!bpIsTablet) {
				$videoElement.removeAttr('src');
			}
		} else {
			if (bpIsDesktop && !hasSource) {
				$videoElement.attr('src', videoURL);
			} else if (!bpIsDesktop) {
				$videoElement.removeAttr('src');
			}
		}
	};
}

/**
 * Plays or pauses the video given certain conditions
 * @param  {Element}  videoElement 		The HTML element of the video
 * @param  {[type]}  	paused       		True if the video is paused
 * @param  {[type]}  	userPaused   		True if the user has paused the video
 * @param  {[type]}  	windowActive 		True if the window for the page is currently active
 * @return {Promise|undefined}        A play() promise or undefined if pausing
 */
function conditionalPlayback(videoElement, userPaused, windowActive) {
	let playPromise;

	try {
		const paused = videoElement.paused;
		const isInView = isElementInView(videoElement, 50);

		if (isInView && !userPaused && windowActive) {
			if (paused) {
				playPromise = videoElement.play().catch(() => {
					$(videoElement).hide();
				});
			}
		} else {
			try {
				if (!paused) {
					playPromise.finally(() => videoElement.pause());
				}
			} catch (e) {
				// Do nothing
			}
		}
	} catch (e) {
		// Do nothing
	}
	return playPromise;
}

/**
 * Returns a function that toggles auto play for video
 * only when it gets visible on viewport
 * @param {Element} videoElement
 * @return {undefined}
 */
const getAutoPlay = videoElement => {
	const initVideo = getInitVideo(videoElement);
	let playPromise;

	return (windowActive = true) => {
		initVideo();
		const videoSource = $(videoElement).attr('src');
		if (videoSource && videoSource.length > 0) {
			const $videoElement = $(videoElement);
			const userPaused = $videoElement.hasClass(classes.paused);

			if (playPromise !== undefined) {
				playPromise
					.then(() => {
						playPromise = conditionalPlayback(videoElement, userPaused, windowActive);
					})
					.catch(
						() =>
							(playPromise = conditionalPlayback(
								videoElement,
								userPaused,
								windowActive
							))
					);
			} else {
				playPromise = conditionalPlayback(videoElement, userPaused, windowActive);
			}
		}
	};
};

/**
 * Initializes all the videos on the page and adds events to reinitialize/pause on
 * resize, scroll and window blur/focus.
 * @param  {String} [selector=selectors.video] The selector for all the video elements
 * @return {undefined}
 */
export default function initVideos(selector = selectors.video) {
	$(selector).each((index, element) => {
		const initAutoPlay = getAutoPlay(element);
		initAutoPlay();

		// Setup the Play/Pause button
		$(element)
			.parent()
			.find(selectors.playBtn)
			.on('click', event => {
				event.stopPropagation();
				toggleVideoPlayback(event.target, element);
			});

		// Check auto play on scroll
		$(window).on('resize.video scroll.video', _debounce(initAutoPlay, 100));

		// Pause the video when the user leaves the tab/window.
		// This fixes a bug in Safari
		$(window).on('blur.video', () => {
			initAutoPlay(false);
		});

		// Play the video when the user returns to the tab/window.
		$(window).on('focus.video', () => {
			initAutoPlay(true);
		});
	});
}

/**
 * Toogles video playback and changes the state of the button
 * @param  {Element} buttonElement 	Button element for play/pause action
 * @param  {Element} videoElement  	Video element for which the action will be performed
 * @return {undefined}
 */
export const toggleVideoPlayback = (buttonElement, videoElement) => {
	try {
		const theme = $(buttonElement).data('theme');
		const $video = $(videoElement);
		if (videoElement.paused) {
			videoElement.play().catch(() => {
				$video.hide();
			});
			triggerAnalyticsEvent('video.play', { element: videoElement });
		} else {
			videoElement.pause();
			triggerAnalyticsEvent('video.pause', { element: videoElement });
		}

		$video.toggleClass(classes.paused);
		$(buttonElement)
			.toggleClass(`${classes.playIcon}${theme}`)
			.toggleClass(`${classes.pauseIcon}${theme}`);
	} catch (e) {
		// Do nothing
	}
};
